<template>
  <SlottedRow
    :allow-delete="editing && displayField.allowDelete"
    :handle-delete="handleDelete"
    :uuid="displayField.uuid"
  >
    <template #label>
      <div
        v-tooltip="{
          text: label,
          disabled: !overflowing,
          openDelay: 800,
          location: 'bottom start',
        }"
      >
        <FieldSuggestions
          id="suggestions"
          ref="suggestions"
          :suggestions="suggestions"
          :current-selection="label"
          :editing="editing && displayField.allowLabelEdit"
          :value-overridden="displayField.valueOverridden"
          placeholder="Custom Field Label"
          data-testid="formatted-row-label"
          @update-selection="handleUpdateLabel"
          @update-overflowing="overflowing = true"
        />
      </div>
    </template>
    <template
      v-if="displayField.citation"
      #icon
    >
      <CitationIcon
        :display-field="displayField"
      />
    </template>
    <template #value>
      <div class="formatted-row-value-wrapper">
        <!-- Renders if we have a number input with a prefix and suffix -->
        <DisplayFieldTextField
          v-if="isNumberInput"
          ref="formatted-row-value-text-field"
          v-model="displayValue"
          :input-type="inputType"
          :placeholder="editingAndEditable ? label : undefined"
          :prefix="editingAndEditable ? prefix : undefined"
          :suffix="editingAndEditable ? suffix : undefined"
          :editing="editingAndEditable"
          @focusin="changeInputType('number')"
          @focusout="changeInputType('text')"
          @blur="checkRowRaw"
        />
        <!-- General case for formatted rows -->
        <template v-else>
          <a
            v-if="isLink && !editing"
            :href="href(value.url)"
            target="_blank"
            class="display-field-text"
          >
            {{ value.text }}
          </a>
          <DisplayFieldTextarea
            v-else
            ref="formatted-row-value-textarea"
            v-model="displayValue"
            :placeholder="editingAndEditable ? label : undefined"
            :editing="editingAndEditable"
            @blur="checkRowRaw"
          />
        </template>
      </div>
    </template>
  </SlottedRow>
</template>

<script>
// Components
import SlottedRow from './SlottedRow.vue';
import DisplayFieldTextField from './DisplayFieldTextField.vue';
import DisplayFieldTextarea from './DisplayFieldTextarea.vue';
import FieldSuggestions from './FieldSuggestions.vue';
import CitationIcon from '@/components/InfoWindow/DisplayFields/CitationIcon.vue';

// Tools & Services
import {DisplayField} from '@/lib/classes/InfoWindowContent';
import {isHyperLink} from '@/lib/ReusableFunctions/Wrappers';
import {Link} from '@/lib/classes/Link';
import {mapState} from 'vuex';

export default {
  name: 'FormattedRow',
  components: {
    SlottedRow,
    DisplayFieldTextField,
    DisplayFieldTextarea,
    FieldSuggestions,
    CitationIcon,
  },
  inject: {
    // contentType can also be thought of as "labelSuggestionSet" and is provided by InfoWindow.vue based on the type
    // of InfoWindow
    contentType: {
      default: '',
    },
  },
  props: {
    displayField: {
      type: DisplayField,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateDisplayField', 'deleteDisplayField'],
  data() {
    return {
      /** @type {string} */
      label: '',
      /** @type {string} */
      value: '',
      // numeric fields typically have an alphanumeric prefix or suffix when displayed, so the display value must toggle
      // between number for input with Vuetify prefix/suffix and text for display with prefix/suffix baked in.
      inputType: 'text',
      suggestions: [],
      // When the delete button is clicked, the row is marked for deletion and the parent component will remove it
      // However, we need to prevent the row from emitting any change events until it is removed to prevent race
      // conditions.
      awaitingDelete: false,
      overflowing: false,
    };
  },
  computed: {
    ...mapState('infowindow', ['fieldSuggestions']),
    unitFormat() {
      return this.displayField.unitFormat();
    },
    isNumberInput() {
      // Right now we check if the unit has a prefix or suffix to see if it is a number input.
      // This should probably be changed to a more explicit check by separating number fields out on
      // the standard fields `unitFormat` object.
      return this.unitFormat?.prefix || this.unitFormat?.suffix;
    },
    isLink() {
      return this.displayField.unit === 'link';
    },
    prefix() {
      return this.unitFormat.prefix ?? '';
    },
    suffix() {
      return this.unitFormat.suffix ?? '';
    },
    editingAndEditable() {
      return this.editing && this.displayField.allowEdit;
    },
    displayValue: {
      get() {
        // If we are not editing or the field is not editable, return the value as a string with prefixes/suffixes
        if (!this.editingAndEditable) return this.displayField.toValueString();
        // if we are editing
        // if the input type is a number, return the bare value
        if (this.inputType === 'number') return this.displayField?.value;
        // for all other types, return the value as a string without prefixes/suffixes
        return this.displayField.toValueString(true);
      },
      set(newVal) {
        // if the value is a hyperlink, set the unit to 'link', if the old unit was 'link' and the value is no longer a
        // hyperlink, set the unit to 'string'. Otherwise, preserve the original unit.
        const isLink = isHyperLink(newVal);
        let unit = isLink ? 'link' : this.displayField.unit;
        if (this.displayField.unit === 'link' && !isLink) {
          unit = 'string';
        }
        this.value = unit === 'link' ? new Link({url: newVal, text: 'Click Here', type: 'url'}) : newVal;
      },
    },
  },
  watch: {
    displayField: {
      handler() {
        // create copies of the label and value to prevent mutating the original displayField
        this.label = this.displayField.label;
        if (this.displayField.unit === 'link') {
          // if the display field is a link, but the value is still a string, convert it to a link
          if (typeof this.displayField.value === 'string') {
            this.value = new Link({
              url: this.displayField.value,
              text: 'Click Here',
              type: 'link',
            });
            return;
          }
          this.value = new Link(this.displayField.value);
          return;
        }
        this.value = this.displayField.value;
      },
      // FormattedRow is used for fields that can be changed from outside the Info Window, such as via a Graphic.
      // We need the watcher to be deep so this.value gets updated properly.
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.suggestions = this.fieldSuggestions[this.contentType] ?? [];
  },
  beforeUnmount() {
  // Must use the raw function to avoid debouncing which
  // will put it on the next tick, too late for the parent to receive the event.
    this.checkRowRaw();
  },
  methods: {
    /**
     * Prepends links with `https://` as needed. Does not modify the URL itself.
     * @param {string} url
     * @return {string}
     */
    href(url) {
      if (url?.startsWith('https://') || url?.startsWith('http://')) return url;
      return 'https://' + url;
    },
    changeInputType(newInputType) {
      this.inputType = newInputType;
    },
    handleUpdateLabel(newLabel) {
      if (!newLabel) return;
      this.label = newLabel;
      this.checkRowRaw();
    },
    handleDelete() {
      this.awaitingDelete = true;
      this.$emit('deleteDisplayField', this.displayField);
    },
    checkRowRaw() {
      // preempt all checks if the row is awaiting deletion
      if (this.awaitingDelete) return;
      // If attempting to save a blank label, restore the original label
      if (this.label === '') {
        this.label = this.displayField.label;
      }

      let unit = this.displayField.unit;

      // don't emit a change event if no change has been made to the original data
      if (
        this.label === this.displayField.label &&
        (
          // bare strings/numbers
          (this.value === this.displayField.value ||
          // incoming link values require deeper comparison
            unit === 'link') &&
            (
            // incoming string url converted to local link object
              this.value?.url === this.displayField.value ||
              // incoming link object copied to local link object
              this.value?.url === this.displayField.value?.url
            )
        )
      ) return;

      // Preserve the original unit. Only modify the unit if the value is now a hyperlink, or was specifically a
      // hyperlink and is no longer.
      const isLink = isHyperLink(this.value?.url);
      if (isLink) {
        unit = 'link';
      } else if (unit === 'link' && !isLink) {
        unit = 'string';
      }

      this.$emit('updateDisplayField', new DisplayField({
        ...this.displayField,
        label: this.label,
        value: this.value,
        unit,
      }));
    },
  },
};
</script>

<style lang="scss" scoped>
.formatted-row-value-wrapper {
  position: relative;
}
</style>
